"use client";

import type { SearchResult } from "@/lib/searchRegistry";
import CountryFlag from "@/components/ui/CountryFlag";
import { Calculator, BookOpen, GitCompare, FileText } from "lucide-react";
import React from "react";

interface SearchResultItemProps {
  item: SearchResult;
  isActive: boolean;
  onClick: () => void;
  searchQuery: string;
}

export default function SearchResultItem({ item, isActive, onClick, searchQuery }: SearchResultItemProps) {
  // Select icon based on item type
  const getIcon = () => {
    if (item.countryCode) {
      return (
        <CountryFlag 
          code={item.countryCode} 
          name={item.countryName || item.countryCode} 
          className="search-result-flag" 
        />
      );
    }

    switch (item.type) {
      case "calculator":
        return <Calculator size={18} className="search-result-type-icon text-primary" />;
      case "blog":
        return <BookOpen size={18} className="search-result-type-icon text-success" />;
      case "comparison":
        return <GitCompare size={18} className="search-result-type-icon text-warning" />;
      case "glossary":
        return <FileText size={18} className="search-result-type-icon text-muted" />;
      default:
        return <FileText size={18} className="search-result-type-icon" />;
    }
  };

  // Helper to highlight matching query text
  const renderHighlightedText = (text: string) => {
    if (!searchQuery.trim()) return text;
    
    // Escape special regex characters
    const escapedQuery = searchQuery.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
    const regex = new RegExp(`(${escapedQuery})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, i) => 
      regex.test(part) ? (
        <mark key={i} className="search-highlight">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  return (
    <div
      onClick={onClick}
      className={`search-result-item ${isActive ? "active" : ""}`}
      role="option"
      aria-selected={isActive}
    >
      <div className="search-result-icon-container">
        {getIcon()}
      </div>
      <div className="search-result-content">
        <div className="search-result-header-row">
          <span className="search-result-title">
            {renderHighlightedText(item.title)}
          </span>
          {item.category && (
            <span className="search-result-category-tag">
              {item.category}
            </span>
          )}
        </div>
        <p className="search-result-description">
          {renderHighlightedText(item.description)}
        </p>
      </div>
    </div>
  );
}
